import React, { useState, useEffect, useCallback } from "react";
import { Session, Mesocycle, ExerciseLogEntry } from "../types";
import { localStorageService, PendingLog } from "../services/localStorage";
import { api } from "../services/api";

interface SessionViewProps {
  mesocycle: Mesocycle;
  session: Session;
  weekNumber: number;
  onBack: () => void;
}

export const SessionView: React.FC<SessionViewProps> = ({
  mesocycle,
  session,
  weekNumber,
  onBack,
}) => {
  const [repsData, setRepsData] = useState<{ [key: string]: number }>({});
  const [weightData, setWeightData] = useState<{ [key: string]: number }>({});
  const [previousWeekLogs, setPreviousWeekLogs] = useState<ExerciseLogEntry[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isFirstWeek = weekNumber === 1;
  const isCompleted = localStorageService.completedSessions.isCompleted(
    mesocycle.id,
    weekNumber,
    session.dayOfWeek
  );

  const loadPendingLogs = useCallback(() => {
    // If session is completed, load from completed sessions
    if (isCompleted) {
      const completedSession = localStorageService.completedSessions.getSession(
        mesocycle.id,
        weekNumber,
        session.dayOfWeek
      );

      if (completedSession) {
        const repsData: { [key: string]: number } = {};
        const weightData: { [key: string]: number } = {};
        completedSession.logs.forEach((log) => {
          const key = `${log.exerciseIndex}-${log.setIndex}`;
          repsData[key] = log.reps;
          if (log.weight !== undefined) {
            weightData[key] = log.weight;
          }
        });
        setRepsData(repsData);
        setWeightData(weightData);
        return;
      }
    }

    // Otherwise load pending logs
    const pendingLogs = localStorageService.pendingLogs.getForSession(
      mesocycle.id,
      weekNumber,
      session.dayOfWeek
    );

    const repsData: { [key: string]: number } = {};
    const weightData: { [key: string]: number } = {};
    pendingLogs.forEach((log) => {
      const key = `${log.exerciseIndex}-${log.setIndex}`;
      repsData[key] = log.reps;
      if (log.weight !== undefined) {
        weightData[key] = log.weight;
      }
    });
    setRepsData(repsData);
    setWeightData(weightData);
  }, [mesocycle.id, session.dayOfWeek, weekNumber, isCompleted]);

  const loadPreviousWeekLogs = useCallback(async () => {
    try {
      const logs = await api.exerciseLogs.getByMesocycleAndSession(
        mesocycle.id,
        weekNumber - 1,
        session.dayOfWeek
      );
      setPreviousWeekLogs(logs);
    } catch (err) {
      console.log("Could not load previous week logs");
    }
  }, [mesocycle.id, session.dayOfWeek, weekNumber]);

  useEffect(() => {
    loadPendingLogs();
    if (!isFirstWeek) {
      loadPreviousWeekLogs();
    }
  }, [isFirstWeek, loadPendingLogs, loadPreviousWeekLogs]);

  const getTargetReps = (exerciseIndex: number, setIndex: number): number => {
    if (isFirstWeek) {
      return session.exercises[exerciseIndex].targetRepsPerSet;
    }

    const prevLog = previousWeekLogs.find(
      (log) => log.exerciseIndex === exerciseIndex && log.setIndex === setIndex
    );

    return prevLog
      ? prevLog.reps
      : session.exercises[exerciseIndex].targetRepsPerSet;
  };

  const getTargetWeight = (
    exerciseIndex: number,
    setIndex: number
  ): number | undefined => {
    if (isFirstWeek) {
      return session.exercises[exerciseIndex].targetWeight;
    }

    const prevLog = previousWeekLogs.find(
      (log) => log.exerciseIndex === exerciseIndex && log.setIndex === setIndex
    );

    return prevLog?.weight || session.exercises[exerciseIndex].targetWeight;
  };

  const handleRepsChange = (
    exerciseIndex: number,
    setIndex: number,
    value: string
  ) => {
    const key = `${exerciseIndex}-${setIndex}`;
    const reps = parseInt(value, 10);

    if (isNaN(reps)) {
      const newData = { ...repsData };
      delete newData[key];
      setRepsData(newData);
      return;
    }

    setRepsData((prev) => ({ ...prev, [key]: reps }));

    const weight = weightData[`${exerciseIndex}-${setIndex}`];
    const log: PendingLog = {
      mesocycleId: mesocycle.id,
      weekNumber,
      dayOfWeek: session.dayOfWeek,
      exerciseIndex,
      setIndex,
      reps,
      ...(weight !== undefined && { weight }),
      timestamp: new Date().toISOString(),
    };

    localStorageService.pendingLogs.save(log);
  };

  const handleWeightChange = (
    exerciseIndex: number,
    setIndex: number,
    value: string
  ) => {
    const key = `${exerciseIndex}-${setIndex}`;
    const weight = parseFloat(value);

    if (isNaN(weight)) {
      const newData = { ...weightData };
      delete newData[key];
      setWeightData(newData);
    } else {
      setWeightData((prev) => ({ ...prev, [key]: weight }));
    }

    const reps = repsData[key];
    if (reps !== undefined) {
      const log: PendingLog = {
        mesocycleId: mesocycle.id,
        weekNumber,
        dayOfWeek: session.dayOfWeek,
        exerciseIndex,
        setIndex,
        reps,
        ...(weight !== undefined && !isNaN(weight) && { weight }),
        timestamp: new Date().toISOString(),
      };
      localStorageService.pendingLogs.save(log);
    }
  };

  const handleCompleteSession = async () => {
    setIsSubmitting(true);

    const logs: ExerciseLogEntry[] = [];
    session.exercises.forEach((exercise, exerciseIndex) => {
      for (let setIndex = 0; setIndex < exercise.sets; setIndex++) {
        const key = `${exerciseIndex}-${setIndex}`;
        const reps = repsData[key] || 0;

        const weight = weightData[key];
        logs.push({
          exerciseIndex,
          exerciseName: exercise.name,
          setIndex,
          reps,
          ...(weight !== undefined && { weight }),
        });
      }
    });

    try {
      await api.exerciseLogs.bulkCreate(
        logs,
        mesocycle.id,
        weekNumber,
        session.dayOfWeek
      );
      const pendingLogs = localStorageService.pendingLogs.getForSession(
        mesocycle.id,
        weekNumber,
        session.dayOfWeek
      );
      localStorageService.completedSessions.markCompleted(
        mesocycle.id,
        weekNumber,
        session.dayOfWeek,
        pendingLogs
      );
      localStorageService.pendingLogs.clearForSession(
        mesocycle.id,
        weekNumber,
        session.dayOfWeek
      );
      onBack();
    } catch (err) {
      alert("Failed to save session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditLog = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    setIsSubmitting(true);

    const logs: ExerciseLogEntry[] = [];
    session.exercises.forEach((exercise, exerciseIndex) => {
      for (let setIndex = 0; setIndex < exercise.sets; setIndex++) {
        const key = `${exerciseIndex}-${setIndex}`;
        const reps = repsData[key] || 0;

        const weight = weightData[key];
        logs.push({
          exerciseIndex,
          exerciseName: exercise.name,
          setIndex,
          reps,
          ...(weight !== undefined && { weight }),
        });
      }
    });

    try {
      // Update the API
      await api.exerciseLogs.bulkCreate(
        logs,
        mesocycle.id,
        weekNumber,
        session.dayOfWeek
      );

      // Update local storage with the new logs
      const updatedLogs: PendingLog[] = logs.map((log) => ({
        mesocycleId: mesocycle.id,
        weekNumber,
        dayOfWeek: session.dayOfWeek,
        exerciseIndex: log.exerciseIndex,
        setIndex: log.setIndex,
        reps: log.reps,
        ...(log.weight !== undefined && { weight: log.weight }),
        timestamp: new Date().toISOString(),
      }));

      // Remove old completed session and add updated one
      const allSessions = localStorageService.completedSessions.getAll();
      const filteredSessions = allSessions.filter(
        (s) =>
          !(
            s.mesocycleId === mesocycle.id &&
            s.weekNumber === weekNumber &&
            s.dayOfWeek === session.dayOfWeek
          )
      );
      filteredSessions.push({
        mesocycleId: mesocycle.id,
        weekNumber,
        dayOfWeek: session.dayOfWeek,
        completedAt: new Date().toISOString(),
        logs: updatedLogs,
      });
      localStorage.setItem(
        "shred-log-completed-sessions",
        JSON.stringify(filteredSessions)
      );

      setIsEditing(false);
      onBack();
    } catch (err) {
      alert("Failed to update session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm">
        <div className="px-4 py-3">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
        <div className="px-4 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">{session.name}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Week {weekNumber} • {session.exercises.length} exercises
          </p>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {session.exercises.map((exercise, exerciseIndex) => (
          <div
            key={exerciseIndex}
            className="bg-white rounded-lg shadow-sm p-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {exercise.name}
            </h3>
            <div className="space-y-2">
              {Array.from({ length: exercise.sets }).map((_, setIndex) => {
                const key = `${exerciseIndex}-${setIndex}`;
                const targetReps = getTargetReps(exerciseIndex, setIndex);
                const targetWeight = getTargetWeight(exerciseIndex, setIndex);
                const actualReps = repsData[key];
                const actualWeight = weightData[key];

                return (
                  <div
                    key={setIndex}
                    className="border-b border-gray-100 last:border-0 pb-2 mb-2"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Set {setIndex + 1}
                      </span>
                      {targetWeight && (
                        <span className="text-xs text-gray-500">
                          Target: {targetReps} reps @ {targetWeight} kgs
                        </span>
                      )}
                      {!targetWeight && (
                        <span className="text-xs text-gray-500">
                          Target: {targetReps} reps
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {targetWeight !== undefined && (
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 block mb-1">
                            Weight
                          </label>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.5"
                            min="0"
                            value={actualWeight || ""}
                            onChange={(e) =>
                              handleWeightChange(
                                exerciseIndex,
                                setIndex,
                                e.target.value
                              )
                            }
                            placeholder={targetWeight?.toString() || "0"}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            disabled={isCompleted && !isEditing}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 block mb-1">
                          Reps
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          value={actualReps || ""}
                          onChange={(e) =>
                            handleRepsChange(
                              exerciseIndex,
                              setIndex,
                              e.target.value
                            )
                          }
                          placeholder={targetReps.toString()}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          disabled={isCompleted && !isEditing}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        {isCompleted && !isEditing ? (
          <button
            onClick={handleEditLog}
            className="w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors bg-gray-500 hover:bg-gray-600"
          >
            Edit Log
          </button>
        ) : (
          <button
            onClick={isEditing ? handleSaveEdit : handleCompleteSession}
            disabled={isSubmitting}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSubmitting
              ? "Saving..."
              : isEditing
              ? "Save Changes"
              : "Complete Session"}
          </button>
        )}
      </div>
    </div>
  );
};
